import React, { useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardMedia,
    IconButton,
    Button,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Fade,
    Snackbar,
    Alert
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import WarningIcon from '@mui/icons-material/Warning';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { theme } from '../../../theme';

const SalonPhotos = ({ salon, onUpload, onDelete, onSetPrimary, onReorder }) => {
    const [uploadOpen, setUploadOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState('');
    const [uploading, setUploading] = useState(false);

    // Delete state
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [photoToDelete, setPhotoToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    // Toast state
    const [toastOpen, setToastOpen] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastSeverity, setToastSeverity] = useState('success');

    const photos = [...(salon.photos || [])].sort((a, b) => (a.display_order || 0) - (b.display_order || 0));

    const getPhotoUrl = (photo) => {
        if (!photo) return '';
        if (typeof photo === 'string') return photo;
        return photo.url || photo.image_url || photo.image || '';
    };

    const handleFileSelect = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setSelectedFile(file);
            setPreviewUrl(URL.createObjectURL(file));
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) return;
        try {
            setUploading(true);
            const formData = new FormData();
            formData.append('image', selectedFile);
            await onUpload(salon.salon_id, formData);
            setUploadOpen(false);
            setSelectedFile(null);
            setPreviewUrl('');
            showToast('Photo ajoutée avec succès', 'success');
        } catch (error) {
            console.error("Upload failed", error);
            showToast('Erreur lors de l\'ajout de la photo', 'error');
        } finally {
            setUploading(false);
        }
    };

    const RequestDelete = (photoId) => {
        setPhotoToDelete(photoId);
        setDeleteConfirmOpen(true);
    };

    const confirmDelete = async () => {
        if (!photoToDelete) return;
        try {
            setDeleting(true);
            await onDelete(salon.salon_id, photoToDelete);
            setDeleteConfirmOpen(false);
            showToast('Photo supprimée', 'success');
        } catch (error) {
            console.error("Delete failed", error);
            showToast('Erreur lors de la suppression', 'error');
        } finally {
            setDeleting(false);
            setPhotoToDelete(null);
        }
    };

    const showToast = (message, severity = 'success') => {
        setToastMessage(message);
        setToastSeverity(severity);
        setToastOpen(true);
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 800, color: theme.colors.text.primary }}>
                    Galerie Photos ({photos.length})
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddPhotoAlternateIcon />}
                    onClick={() => setUploadOpen(true)}
                    sx={{ fontWeight: 700, borderRadius: theme.borderRadius.lg, textTransform: 'none' }}
                >
                    Ajouter
                </Button>
            </Box>

            {photos.length === 0 ? (
                <Box
                    sx={{
                        p: 6,
                        textAlign: 'center',
                        bgcolor: theme.colors.background.secondary,
                        borderRadius: theme.borderRadius.xl,
                        border: `1px dashed ${theme.colors.border.light}`
                    }}
                >
                    <Typography variant="body1" sx={{ color: theme.colors.text.secondary, fontWeight: 500 }}>
                        Aucune photo pour le moment.
                    </Typography>
                </Box>
            ) : (
                <Grid container spacing={2}>
                    {photos.map((photo) => (
                        <Grid item xs={6} sm={4} md={3} key={photo.photo_id}>
                            <Fade in>
                                <Card
                                    sx={{
                                        borderRadius: theme.borderRadius.lg,
                                        position: 'relative',
                                        overflow: 'hidden',
                                        boxShadow: theme.boxShadow.sm,
                                        '&:hover .overlay': { opacity: 1 }
                                    }}
                                >
                                    <CardMedia
                                        component="img"
                                        height="200"
                                        image={getPhotoUrl(photo)}
                                        alt={photo.caption || 'Salon photo'}
                                        sx={{ objectFit: 'cover' }}
                                        referrerPolicy="no-referrer"
                                        onError={() => {
                                            // eslint-disable-next-line no-console
                                            console.error('Salon photo failed to load:', getPhotoUrl(photo));
                                        }}
                                    />

                                    {/* Reorder Arrows - Vertical Center */}
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: '50%',
                                            left: 0,
                                            right: 0,
                                            transform: 'translateY(-50%)',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            px: 1,
                                            zIndex: 2,
                                            pointerEvents: 'none',
                                            '& .MuiIconButton-root': {
                                                pointerEvents: 'auto',
                                                bgcolor: 'rgba(255,255,255,0.8)',
                                                '&:hover': { bgcolor: 'white' },
                                                '&.Mui-disabled': { bgcolor: 'rgba(255,255,255,0.3)' }
                                            }
                                        }}
                                    >
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                const currentIndex = photos.findIndex(p => p.photo_id === photo.photo_id);
                                                if (currentIndex > 0) {
                                                    const newPhotos = [...photos];
                                                    [newPhotos[currentIndex - 1], newPhotos[currentIndex]] = [newPhotos[currentIndex], newPhotos[currentIndex - 1]];
                                                    const orderedIds = newPhotos.map(p => p.photo_id);
                                                    onReorder(orderedIds);
                                                }
                                            }}
                                            disabled={photos.findIndex(p => p.photo_id === photo.photo_id) === 0}
                                        >
                                            <ArrowBackIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                const currentIndex = photos.findIndex(p => p.photo_id === photo.photo_id);
                                                if (currentIndex < photos.length - 1) {
                                                    const newPhotos = [...photos];
                                                    [newPhotos[currentIndex + 1], newPhotos[currentIndex]] = [newPhotos[currentIndex], newPhotos[currentIndex + 1]];
                                                    const orderedIds = newPhotos.map(p => p.photo_id);
                                                    onReorder(orderedIds);
                                                }
                                            }}
                                            disabled={photos.findIndex(p => p.photo_id === photo.photo_id) === photos.length - 1}
                                        >
                                            <ArrowForwardIcon fontSize="small" />
                                        </IconButton>
                                    </Box>

                                    {/* Hover Overlay */}
                                    <Box
                                        className="overlay"
                                        sx={{
                                            position: 'absolute',
                                            top: 0, left: 0, right: 0, bottom: 0,
                                            bgcolor: 'rgba(0,0,0,0.5)',
                                            opacity: 0,
                                            transition: 'opacity 0.2s',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: 1,
                                            zIndex: 1
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <IconButton
                                                onClick={() => onSetPrimary(photo.photo_id)}
                                                sx={{
                                                    bgcolor: 'white',
                                                    color: photo.is_primary ? theme.colors.warning[500] : theme.colors.text.tertiary,
                                                    '&:hover': { bgcolor: theme.colors.primary[50] }
                                                }}
                                                title={photo.is_primary ? "Photo principale" : "Définir comme principale"}
                                            >
                                                {photo.is_primary ? <StarIcon /> : <StarBorderIcon />}
                                            </IconButton>
                                            <IconButton
                                                onClick={() => RequestDelete(photo.photo_id)}
                                                sx={{
                                                    bgcolor: 'white',
                                                    color: theme.colors.error[500],
                                                    '&:hover': { bgcolor: theme.colors.error[50] }
                                                }}
                                                title="Supprimer"
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Box>
                                    </Box>

                                    {/* Primary Indicator (Always Visible if Primary) */}
                                    {photo.is_primary && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: 8,
                                                left: 8,
                                                bgcolor: theme.colors.warning[500],
                                                color: 'white',
                                                borderRadius: '50%',
                                                p: 0.5,
                                                boxShadow: theme.boxShadow.sm,
                                                zIndex: 3
                                            }}
                                        >
                                            <StarIcon fontSize="small" />
                                        </Box>
                                    )}
                                </Card>
                            </Fade>
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* Upload Dialog */}
            <Dialog open={uploadOpen} onClose={() => setUploadOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle sx={{ fontWeight: 800 }}>Ajouter une photo</DialogTitle>
                <DialogContent>
                    <Box
                        sx={{
                            border: `2px dashed ${theme.colors.border.light}`,
                            borderRadius: theme.borderRadius.xl,
                            p: 4,
                            textAlign: 'center',
                            cursor: 'pointer',
                            bgcolor: theme.colors.background.secondary,
                            mt: 1,
                            '&:hover': { borderColor: theme.colors.primary[300], bgcolor: theme.colors.primary[50] }
                        }}
                        onClick={() => document.getElementById('photo-upload-input').click()}
                    >
                        <input
                            type="file"
                            id="photo-upload-input"
                            hidden
                            accept="image/*"
                            onChange={handleFileSelect}
                        />

                        {previewUrl ? (
                            <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                <img src={previewUrl} alt="Preview" style={{ maxHeight: 200, maxWidth: '100%', borderRadius: 8 }} />
                                <Button sx={{ display: 'block', mx: 'auto', mt: 1 }}>Changer</Button>
                            </Box>
                        ) : (
                            <>
                                <CloudUploadIcon sx={{ fontSize: 48, color: theme.colors.text.tertiary, mb: 1 }} />
                                <Typography variant="body1" sx={{ fontWeight: 600, color: theme.colors.text.primary }}>
                                    Cliquez pour sélectionner une image
                                </Typography>
                                <Typography variant="caption" sx={{ color: theme.colors.text.tertiary }}>
                                    JPG, PNG acceptés
                                </Typography>
                            </>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setUploadOpen(false)} sx={{ fontWeight: 700 }}>Annuler</Button>
                    <Button
                        variant="contained"
                        onClick={handleUpload}
                        disabled={!selectedFile || uploading}
                        sx={{ fontWeight: 800 }}
                    >
                        {uploading ? 'Envoi...' : 'Télécharger'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirmation Dialog */}
            <Dialog
                open={deleteConfirmOpen}
                onClose={() => setDeleteConfirmOpen(false)}
                PaperProps={{
                    sx: { borderRadius: theme.borderRadius.lg, p: 1 }
                }}
            >
                <DialogContent sx={{ textAlign: 'center', pt: 3 }}>
                    <Box sx={{ color: theme.colors.warning[500], mb: 2 }}>
                        <WarningIcon sx={{ fontSize: 48 }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
                        Confirmer la suppression
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.colors.text.secondary }}>
                        Voulez-vous vraiment supprimer cette photo ? Cette action est irréversible.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
                    <Button
                        onClick={() => setDeleteConfirmOpen(false)}
                        sx={{ fontWeight: 700, color: theme.colors.text.secondary }}
                    >
                        Annuler
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={confirmDelete}
                        disabled={deleting}
                        sx={{ fontWeight: 700, px: 3 }}
                    >
                        {deleting ? 'Suppression...' : 'Supprimer'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Toast Notification */}
            <Snackbar
                open={toastOpen}
                autoHideDuration={4000}
                onClose={() => setToastOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setToastOpen(false)}
                    severity={toastSeverity}
                    sx={{ width: '100%', fontWeight: 600, borderRadius: theme.borderRadius.md }}
                    variant="filled"
                >
                    {toastMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default SalonPhotos;
